import {useEffect, useState} from 'react';
import {Col, Row} from 'react-bootstrap';
import ProgressBar from 'react-bootstrap/ProgressBar';
import {useNavigate} from 'react-router-dom';
import {Sync, getCatalogsData} from '../../../../api';
import {CITY, ONBOARDING_KEYS, PROVINCE, UNITS, fetchFileData, formatNum} from '../../../../constants';
import {useAuthContext, useLanguageContext, useSessionContext, useThemeContext} from '../../../../context';
import {BackHeader, Button, CustomContainer, FooterLogo, Loading, TextField} from '../../../common';
import {bulkCreateCatalogs, bulkCreateCategory} from '../../../../pos-core/database/helpers';

const BusinessDetails = () => {
    const navigate = useNavigate();

    const {user, business, setBusiness, handleIncrementCounter, account} = useAuthContext();
    const {handleCreateSession, handleCloseSession} = useSessionContext();
    const {I18n} = useLanguageContext();
    const {theme} = useThemeContext();

    const [isLoading, setIsLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [data, setData] = useState([]);

    const [formData, setFormData] = useState({
        name: '',
        kind: '',
        type: '',
        province: '',
        city: '',
        address: '',
        location_type: '',
        location_name: 'Headquarter',
    });

    const businessDetails = [
        {
            id: 'initialInfo',
            title: 'Tell us a little about your business',
            subHeader: 'We’ll help you get started based on your selection',
            fields: [
                {
                    label: I18n.what_kind_of_business_are_you,
                    type: 'select',
                    options: [{name: 'Single store'}, {name: 'Franchise'}],
                    stateKey: 'kind',
                },
            ],
        },
        {
            id: 'about',
            title: 'Tell us a little about your business',
            subHeader: 'Every business is unique. We want to hear about yours.',
            fields: [
                {
                    label: I18n.what_is_your_business_type,
                    type: 'select',
                    options: [{name: 'Retail'}, {name: 'Restaurant'}, {name: 'Saloon'}],
                    stateKey: 'type',
                },
                {
                    label: I18n.what_is_your_business_name,
                    type: 'text',
                    stateKey: 'name',
                },
            ],
        },
        {
            id: 'locationInfo',
            title: 'Setup your location',
            subHeader: 'Please setup your location and business address',
            fields: [
                {
                    label: I18n.location_name,
                    type: 'text',
                    stateKey: 'location_name',
                    disabled: true,
                },
                {
                    label: I18n.location_type,
                    type: 'select',
                    options: [{name: 'Physical location'}, {name: 'Mobile location'}],
                    stateKey: 'location_type',
                },
            ],
        },
        {
            id: 'location',
            title: 'Setup your location',
            subHeader: 'What’s your business location?',
            fields: [
                {
                    label: I18n.province,
                    type: 'select',
                    options: PROVINCE,
                    stateKey: 'province',
                },
                {
                    label: I18n.city,
                    type: 'select',
                    options: CITY,
                    stateKey: 'city',
                },
                {
                    label: I18n.address,
                    type: 'text',
                    stateKey: 'address',
                },
            ],
        },
    ];

    const toggleIndex = val => setActiveIndex(val);

    const onChange = (label, value) => {
        handleChange(label, value);
    };

    const handleNext = async () => {
        if (activeIndex < 3) {
            if (activeIndex === 0 && formData?.kind.name === 'Single store') {
                const temp = data?.map(item => {
                    if (item.id === 'locationInfo') {
                        return {
                            ...item,
                            fields: item.fields.map(field => ({...field, disabled: false})),
                        };
                    } else {
                        return item;
                    }
                });
                setData(temp);
                setFormData(prev => ({...prev, location_name: ''}));
            }
            setActiveIndex(x => x + 1);
        } else {
            handleSave();
        }
    };

    const handleSave = async () => {
        try {
            console.log('account', account);
            console.log('form data', formData);

            setIsLoading(true);
            let paramsForBusiness = {
                ...formData,
                isHq: true,
                phone: account.phone_number,
                email: account.email,
                kind: formData.kind?.name,
                location_type: formData.location_type?.name,
                city: formData.city?.name,
                province: formData.province?.name,
                type: formData.type?.name,
            };
            console.log('paramsForBusiness', paramsForBusiness);
            business.updateDetails(paramsForBusiness).then(async res => {
                setBusiness(res);
            });

            let apiResponse = await getCatalogsData({
                // catalog_name: "MedicineCatalog",
                catalog_name: 'null',
            });
            if (apiResponse.status === 200) {
                const catalogData = apiResponse?.data?.response;
                const uniqueCategories = new Set(catalogData.map(item => item.category?.toLowerCase()));
                const uniqueCategoriesArray = [...uniqueCategories];
                await bulkCreateCategory(uniqueCategoriesArray, account);

                const allCategories = await account?.category?.fetch();
                const aggregatedData = catalogData?.map(item => {
                    const productCategory = allCategories?.find(category => category?.name?.toLowerCase() === item?.category?.toLowerCase());
                    const {name, price, cost_per_item, add_qty, low_stock_alert, barcode, unit} = item;

                    let selling = Number(price) || 0;
                    let costing = Number(cost_per_item) || 0;
                    let totalProfit = selling - costing;
                    let totalMargin = (totalProfit / costing) * 100;
                    let profit = totalProfit <= 0 ? '0' : totalProfit;
                    let margin = totalMargin <= 0 || totalMargin == Infinity ? '0' : formatNum(totalMargin);
                    let itemUnit = UNITS?.find(itemUnit => itemUnit?.name?.toLowerCase() === unit?.toLowerCase())?.name;

                    let productObj = {
                        cost_price: Number(cost_per_item) || 0,
                        is_taxable: false,
                        is_trackable: false,
                        name,
                        rate: 0,
                        secondary_unit: '',
                        unit: itemUnit || '',
                        barcode: barcode || '',
                        low_stock: Number(low_stock_alert),
                        margin: Number(margin),
                        profit: Number(profit),
                        quantity: Number(add_qty) || 0,
                        selling_price: Number(price) || 0,
                        tax: '',
                        category: productCategory,
                    };
                    return productObj;
                });
                await bulkCreateCatalogs(aggregatedData, account);
            }
            await Sync(user.username);

            let paramsForSession = {
                business,
                user,
                is_active: true,
                opening_balance: 0,
                started_at: new Date().getTime(),
            };

            handleCreateSession(paramsForSession);
            handleIncrementCounter('session');

            setIsLoading(false);
            navigate('/onboarding');
        } catch (error) {
            console.log('something went wrong', error.message);
        }
    };

    const handleEnterPress = event => {
        if (event.key === 'Enter') {
            handleNext();
        }
    };

    const handleChange = (key, value) => {
        const getKeyName = ONBOARDING_KEYS[key];
        setFormData(prev => ({...prev, [getKeyName]: value}));
    };

    const renderFields = () => {
        return data[activeIndex]?.fields.map(field => (
            <TextField
                key={field.label}
                onChange={onChange}
                label={field.label}
                placeholder={field.label}
                value={formData[field.stateKey]}
                data={field.options}
                inputType={field.type}
                type="text"
                disabled={field.disabled}
            />
        ));
    };

    useEffect(() => {
        setData(businessDetails);
    }, []);

    return (
        <>
            <CustomContainer handlePress={handleEnterPress} className="signupCont">
                <Row className="justify-content-md-center">
                    <Col md={3} className="progressbarMainBox">
                        <div className="progressbarMainBoxInner">
                            <ProgressBar onClick={() => toggleIndex(0)} now={activeIndex == 0 ? 100 : 0} />
                            <ProgressBar onClick={() => toggleIndex(1)} now={activeIndex == 1 ? 100 : 0} />
                            <ProgressBar onClick={() => toggleIndex(2)} now={activeIndex == 2 ? 100 : 0} />
                            <ProgressBar onClick={() => toggleIndex(3)} now={activeIndex == 3 ? 100 : 0} />
                        </div>
                    </Col>

                    <BackHeader type="skip" action={handleNext} />
                </Row>

                <Row className="justify-content-md-center">
                    <Col md={5}>
                        <div className="loginFormBox">
                            <div>
                                <h1 className="fontSize24 fontWeight700 " style={{color: theme.text}}>
                                    {data[activeIndex]?.title}
                                </h1>
                                <p className="fontSize14 fontWeight400 darkGrayTwo marBot0">{data[activeIndex]?.subHeader}</p>
                            </div>

                            <div className="singupFields businessCreationFields">{renderFields()}</div>
                            <div className="marTop15">
                                <Button className={`width100 fontWeight600 fontSize16 cursorPointer`} title={I18n.continue} handleClick={handleNext} />
                            </div>
                        </div>
                    </Col>
                </Row>
            </CustomContainer>

            {isLoading && <Loading heading={I18n.starting_pos} subHeading={I18n.start_selling} preHeading={I18n.thank_you} />}
        </>
    );
};

export default BusinessDetails;
